import { Component, computed, effect, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthUserDto } from '@artemis-software/wr-api';
import { CurrentUserService } from '@services/current-user.service';
import { queryParam } from '@/utils/signals/router';
import { currentTenant, isExternalTenant, isInternalTenant } from '@/utils/admin-utils';
import { ContractorAttachmentFolderViewComponent } from '@components/contractor-attachment-folder-view/contractor-attachment-folder-view.component';
import TenantTypeEnum = AuthUserDto.TenantTypeEnum;

@Component({
  standalone: false,
  selector: 'wr-contractor-attachment-folder-configuration-page',
  templateUrl: './contractor-attachment-folder-configuration-page.component.html',
  styleUrls: ['./contractor-attachment-folder-configuration-page.component.scss'],
})
export class ContractorAttachmentFolderConfigurationPageComponent {
  @ViewChild(ContractorAttachmentFolderViewComponent) folderView?: ContractorAttachmentFolderViewComponent;

  readonly queryExternalTenantId = queryParam('externalTenantId', false);
  readonly currentTenant = currentTenant();
  readonly isInternalTenant = isInternalTenant();
  readonly isExternalTenant = isExternalTenant();
  readonly selectedExternalTenantId = signal<string | undefined>(undefined);
  readonly isEditing = signal(false);
  readonly canEdit = computed(() =>
    this.currentUserService.hasPrivilege('editContractorAttachmentFolderConfig') && !!this.selectedExternalTenantId(),
  );

  filterForm!: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly currentUserService: CurrentUserService,
  ) {
    this.filterForm = this.formBuilder.group({
      tenantId: this.formBuilder.control<string | undefined>(undefined),
    });

    this.filterForm.get('tenantId')?.valueChanges.subscribe(value => this.selectedExternalTenantId.set(value));

    effect(() => {
      const isExternalTenant = this.isExternalTenant();
      const currentTenant = this.currentTenant();
      if (isExternalTenant && currentTenant) {
        this.selectedExternalTenantId.set(currentTenant.id);
      }
    });

    effect(() => {
      const queryExternalTenantId = this.queryExternalTenantId();
      if (queryExternalTenantId) {
        this.filterForm.get('tenantId')?.setValue(queryExternalTenantId);
      }
    });

    effect(() => {
      this.selectedExternalTenantId();
      this.isEditing.set(false);
    });
  }

  startEdit(): void {
    this.isEditing.set(true);
  }

  async saveEdit(): Promise<void> {
    if (!this.folderView) return;
    const success = await this.folderView.submit();
    if (success) {
      this.isEditing.set(false);
    }
  }

  cancelEdit(): void {
    this.folderView?.cancel();
    this.isEditing.set(false);
  }

  protected readonly TenantTypeEnum = TenantTypeEnum;
}
